import {
    createContext,
    useState,
    useEffect,
    FC,
    ReactNode,
    useMemo,
} from "react";
import { getTokenPayload } from "../../utils/jwt";
import { useLocation, useNavigate } from "react-router-dom";
import { formatPermissions } from "../../utils/permissions";
import { Action, Resource } from "../../types/acl";
import { AuthUser } from "../../types/User";
import { Role } from "../../types/enums/Role";
import {
    loginService,
    refreshTokenService,
    verifyTokenService,
} from "../../services/api/auth";

function getStreamModeCookieValue(): string | undefined {
    return document.cookie
        .split("; ")
        .find((row) => row.startsWith("isStreamMode="))
        ?.split("=")[1];
}

/**
 * Stream mode : par défaut ON pour la plupart des rôles (cookie absent = ON),
 * sauf pour les formateurs : cookie absent = OFF (pas de diffusion par défaut).
 */
function getInitialStreamOn(): boolean {
    const cookieVal = getStreamModeCookieValue();
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
        try {
            const { role } = getTokenPayload(accessToken);
            if (role === Role.Formateur) {
                if (cookieVal === undefined || cookieVal === "") {
                    return false;
                }
                return cookieVal === "true";
            }
        } catch {
            // token illisible : fallback ci-dessous
        }
    }
    return cookieVal !== "false";
}

interface AuthContextType {
    user?: AuthUser;
    isAuthenticated: boolean;
    loading: boolean;
    login: (email: string, password: string) => void;
    refreshAccessToken: () => void;
    setUser: (user: AuthUser) => void;
    hasPermission: (resource: Resource, action: Action) => boolean;
    hasResource: (resource: Resource) => boolean;
    getName: () => string | undefined;
    getNameInitials: () => string | undefined;
    getIsOwnDashboard: () => boolean;
    getUserIdFromQuery: () => string | null;
    isStreamOn: boolean;
    handleIsStreamOn: (value: boolean) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
    undefined
);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<AuthUser | undefined>();
    const [isStreamOn, setIsStreamOn] = useState<boolean>(getInitialStreamOn);

    const noAuthRoutes = useMemo(
        () => [
            "/",
            "/login",
            "/register",
            "/forgot-password",
            "/reset-password",
        ],
        []
    );

    const getSafePostLoginRedirect = () => {
        const fallback = "/trades";
        const rawRedirect = sessionStorage.getItem("postLoginRedirect");

        if (!rawRedirect) return fallback;
        sessionStorage.removeItem("postLoginRedirect");

        const redirect = rawRedirect.trim();
        if (!redirect.startsWith("/") || redirect.startsWith("//")) {
            return fallback;
        }

        try {
            const parsed = new URL(redirect, window.location.origin);
            if (parsed.origin !== window.location.origin) {
                return fallback;
            }
            if (noAuthRoutes.includes(parsed.pathname)) {
                return fallback;
            }
            return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        } catch {
            return fallback;
        }
    };

    useEffect(() => {
        if (user) {
            setIsAuthenticated(true);
            setLoading(false);
        }
    }, [user]);

    // Formateurs : si aucun cookie n'a encore été posé, persister explicitement stream OFF
    useEffect(() => {
        if (!user || user.role !== Role.Formateur) return;
        const cookieVal = getStreamModeCookieValue();
        if (cookieVal === undefined || cookieVal === "") {
            document.cookie =
                "isStreamMode=false; path=/; max-age=31536000"; // 1 an
            setIsStreamOn(false);
        }
    }, [user]);

    const setInfosFromToken = (accessToken: string) => {
        const {
            id,
            pseudo,
            firstname,
            lastname,
            email,
            role,
            permissions,
            termsAcceptedAt,
            promotionId,
        } = getTokenPayload(accessToken);

        setUser({
            id,
            firstname,
            lastname,
            pseudo,
            email,
            role,
            permissions: formatPermissions(permissions),
            termsAcceptedAt,
            promotionId: promotionId ?? null,
        });
    };

    const refreshAccessToken = async () => {
        const refreshToken = localStorage.getItem("refreshToken");

        if (!refreshToken) {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            setLoading(false);
            return;
        }

        try {
            const {
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
            } = await refreshTokenService(refreshToken);

            if (newAccessToken && newRefreshToken) {
                localStorage.setItem("accessToken", newAccessToken);
                localStorage.setItem("refreshToken", newRefreshToken);

                setInfosFromToken(newAccessToken);

                if (noAuthRoutes.includes(window.location.pathname)) {
                    sessionStorage.setItem("showProfileIncompleteModal", "1");
                    navigate(getSafePostLoginRedirect());
                }
            } else {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
            }
        } catch {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
        }
    };

    const verifyToken = async (accessToken: string) => {
        try {
            const data = await verifyTokenService(accessToken);

            if (data.message === "Token valide.") {
                if (noAuthRoutes.includes(window.location.pathname)) {
                    sessionStorage.setItem("showProfileIncompleteModal", "1");
                    navigate(getSafePostLoginRedirect());
                }

                setInfosFromToken(accessToken);
            } else {
                await refreshAccessToken();
            }
        } catch {
            await refreshAccessToken();
        }
    };

    useEffect(() => {
        const accessToken = localStorage.getItem("accessToken");
        const refreshToken = localStorage.getItem("refreshToken");

        if (accessToken) {
            verifyToken(accessToken);
        } else if (refreshToken) {
            refreshAccessToken();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // useEffect(() => {
    //     const accessToken = localStorage.getItem("accessToken");
    //     const refreshToken = localStorage.getItem("refreshToken");

    //     if (accessToken) {
    //         verifyToken(accessToken);
    //     } else if (refreshToken) {
    //         refreshAccessToken();
    //     } else {
    //         setLoading(false);
    //     }
    // }, [navigate, noAuthRoutes, refreshAccessToken]);

    const login = async (email: string, password: string) => {
        const { accessToken, refreshToken } = await loginService(email, password);

        if (accessToken && refreshToken) {
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", refreshToken);

            const { role } = getTokenPayload(accessToken);
            if (role === Role.Formateur) {
                document.cookie =
                    "isStreamMode=false; path=/; max-age=31536000"; // 1 an
                setIsStreamOn(false);
            } else {
                document.cookie =
                    "isStreamMode=true; path=/; max-age=31536000"; // 1 an
                setIsStreamOn(true);
            }

            setInfosFromToken(accessToken);

            sessionStorage.setItem("showProfileIncompleteModal", "1");
            navigate(getSafePostLoginRedirect());
        }
    };

    const hasPermission = (resource: Resource, action: Action) => {
        if (!user) return false;

        return user.permissions.some(
            (permission) =>
                permission.resource === resource &&
                permission.actions.includes(action)
        );
    };

    const hasResource = (resource: Resource) => {
        if (!user) return false;

        return user.permissions.some(
            (permission) => permission.resource === resource
        );
    };

    const getName = () => {
        if (user?.firstname && user?.lastname) {
            return `${user.firstname} ${user.lastname}`;
        }
        return user?.pseudo;
    };

    const getNameInitials = () => {
        if (user?.firstname && user?.lastname) {
            return `${user.firstname.charAt(0)}${user.lastname.charAt(0)}`;
        }
        return user?.pseudo.charAt(0);
    };

    const getIsOwnDashboard = () => {
        return !queryParams.has("userId");
    };

    const getUserIdFromQuery = () => {
        return queryParams.has("userId") &&
            hasPermission("admin-trading-sessions", "read")
            ? queryParams.get("userId")
            : user!.id;
    };

    const handleIsStreamOn = (value: boolean) => {
        document.cookie = `isStreamMode=${value}; path=/; max-age=31536000`; // 1 year
        setIsStreamOn(value);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated,
                loading,
                login,
                refreshAccessToken,
                setUser,
                hasPermission,
                hasResource,
                getName,
                getNameInitials,
                getIsOwnDashboard,
                getUserIdFromQuery,
                isStreamOn,
                handleIsStreamOn,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
